/**
 * PDP Mobile v2 - JavaScript
 * Modern product page functionality
 * Foundation for variant system, upsells, and sticky CTA
 * Last updated: 2025-10-17
 */

(function(window, document) {
  'use strict';

  /**
   * PDPManager - Main product page controller
   * Phase 1: Basic gallery and form interactions
   * Phase 2: Will add variant availability matrix
   * Phase 3: Will add upsell filtering
   */
  /**
   * Pure helpers for the one-tap add-on row. Kept free of DOM so they can be
   * exercised in Node (tests/pdp-addon.test.mjs) the way the cart ladder is.
   */
  const PDPV2Helpers = {
    // Shopify's own single-variant sentinel plus the hand-set generic this
    // catalogue uses (the Support Cushion Insert). A variant carrying either
    // is "the" variant of a product that has no finish to match.
    GENERIC_OPTION_VALUES: ['standard', 'default title'],

    isGenericOption(value) {
      return typeof value === 'string' && PDPV2Helpers.GENERIC_OPTION_VALUES.includes(value.trim().toLowerCase());
    },

    // Resolve the add-on variant for the main product's selected option value
    // (finish). Exact match on option1 (case/space-insensitive) wins; a
    // product that carries no variant in that finish at all falls back to its
    // generic variant (Standard / Default Title); otherwise null.
    // Sold out at either step is null: the row hides rather than offering
    // something that cannot be added, and never swaps one finish for another.
    // Returns { variant, byFinish } so the caller can pick the label copy.
    resolveAddonVariant(variants, optionValue) {
      if (!Array.isArray(variants)) return null;
      const want = typeof optionValue === 'string' ? optionValue.trim().toLowerCase() : null;
      const named = want === null ? null
        : variants.find(v => v && typeof v.option1 === 'string' && v.option1.trim().toLowerCase() === want);
      // A match on the generic value itself (a single-variant MAIN product
      // meeting a single-variant add-on) is not a finish: no "in Default Title".
      if (named) return named.available ? { variant: named, byFinish: !PDPV2Helpers.isGenericOption(named.option1) } : null;
      // Prefer an AVAILABLE generic: a product listing "Default Title" (sold
      // out) before "Standard" must not hide the row over the wrong one.
      const generic = variants.find(v => v && v.available && PDPV2Helpers.isGenericOption(v.option1));
      return generic ? { variant: generic, byFinish: false } : null;
    },

    // Section Rendering API URL for the cart gate. Shopify.routes.root is
    // locale-aware ("/", "/fr-ca/") but its trailing slash is not something to
    // rely on (routes.root_url is inconsistent, see feedback memory).
    cartTypesSectionUrl(root) {
      const base = typeof root === 'string' && root ? root : '/';
      return base.replace(/\/?$/, '/') + '?section_id=pdp-addon-cart-types';
    },

    // Variant-only view of resolveAddonVariant (kept for callers and tests).
    matchAddonVariant(variants, optionValue) {
      const hit = PDPV2Helpers.resolveAddonVariant(variants, optionValue);
      return hit ? hit.variant : null;
    },

    // Product types present in a /cart.js payload. Bundle lines only report
    // the bundle's own type here: the Ajax cart carries no item_components,
    // so this is the optimistic view; the section render is the full one.
    cartPayloadTypes(cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.map(item => item && item.product_type).filter(t => typeof t === 'string');
    },

    // Whether any cart product type is on the row's hide list (the product's
    // custom.pdp_addon_hide_when_cart_has metafield). Case/space-insensitive;
    // an empty or missing list never blocks.
    cartBlocksAddon(cartTypes, hideTypes) {
      if (!Array.isArray(cartTypes) || !Array.isArray(hideTypes) || !hideTypes.length) return false;
      const norm = v => (typeof v === 'string' ? v.trim().toLowerCase() : '');
      const hide = new Set(hideTypes.map(norm).filter(Boolean));
      return cartTypes.some(t => hide.has(norm(t)));
    },

    // Parse the pdp-addon-cart-types section render (Section Rendering API):
    // a JSON array of product types, bundle components included. Anything
    // unparseable is null so the caller keeps its current state.
    parseCartTypesSection(html) {
      if (typeof html !== 'string') return null;
      const m = html.match(/<script[^>]*data-cart-types[^>]*>([\s\S]*?)<\/script>/);
      if (!m) return null;
      try {
        const parsed = JSON.parse(m[1]);
        return Array.isArray(parsed) ? parsed.filter(t => typeof t === 'string') : null;
      } catch (e) { return null; }
    },

    // Percent the ladder grants at `count` eligible units. Tiers are sorted
    // by minCount DESCENDING in BundleConfig; first match wins, 0 otherwise.
    tierPercent(tiers, count) {
      if (!Array.isArray(tiers)) return 0;
      const tier = tiers.find(t => t && count >= t.minCount);
      return tier ? Number(tier.percent) || 0 : 0;
    },

    // Units the ladder will see once this form is submitted with the add-on:
    // the main quantity, plus one add-on, plus whatever eligible units are
    // already in the cart (the ladder counts the whole cart, not this add).
    // Only allowlisted product types count, so a row enabled on a product the
    // ladder ignores never promises a tier the cart will not grant.
    unitCount(quantityValue, cartUnits = 0, mainEligible = true, addonEligible = true) {
      const qty = Math.max(1, parseInt(quantityValue, 10) || 1);
      return (mainEligible ? qty : 0) + (addonEligible ? 1 : 0) + (Number(cartUnits) || 0);
    },

    // Percent the row may promise for ticking the box. 0 (hide) when the
    // add-on itself is not on the ladder: the cart may still reach a tier
    // through other units, but "X% off both at cart" would then describe a
    // discount the add-on never receives (Codex review, PR #291).
    addonDiscountPercent({ tiers, quantityValue, cartUnits, mainEligible, addonEligible }) {
      if (!addonEligible) return 0;
      const count = PDPV2Helpers.unitCount(quantityValue, cartUnits, mainEligible, addonEligible);
      return PDPV2Helpers.tierPercent(tiers, count);
    },

    // Whether a product_type is on the ladder's allowlist (the drawer's
    // _isQualifying). Unknown config -> not eligible, never a guess.
    typeEligible(productType, config) {
      const types = config && config.qualifying && config.qualifying.types;
      return Array.isArray(types) && types.includes(productType);
    },

    // Eligible units already in a /cart.js payload, by the same product_type
    // allowlist bundle-discounts.js uses (its _countQualifying). Tolerates any
    // shape a `cart:updated` dispatcher might send.
    cartEligibleUnits(cart, config) {
      const types = config && config.qualifying && config.qualifying.types;
      if (!cart || !Array.isArray(cart.items) || !Array.isArray(types)) return 0;
      return cart.items
        .filter(item => item && types.includes(item.product_type))
        .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    },

    // The ladder is an offer only while its orchestrator is enabled; when
    // bundle-discounts.js exits early on `enabled: false` nothing in the cart
    // will grant it, so the row must not promise it.
    ladderTiers(config) {
      if (!config || config.enabled === false) return null;
      return config.tiers;
    },

    // Format `cents` the way the server formatted `sample`, the market's
    // rendering of 1234567.89 (Liquid `123456789 | money`). Derives prefix,
    // suffix, thousands and decimal separators, and whether decimals are
    // shown at all — so "€1.234.567,89" yields "€17,95" and "$1,234,567.89"
    // yields "$17.95". Returns null when the sample is not recognisable.
    moneyFromSample(cents, sample) {
      if (typeof sample !== 'string') return null;
      const m = /^(.*?)(\d(?:[^\d]?\d)*)(.*)$/.exec(sample.trim());
      if (!m) return null;
      const [, prefix, digits, suffix] = m;
      const seps = digits.replace(/\d/g, '');
      // Decimals are shown iff the digit run ends "<sep>89"; that sep is the
      // decimal separator, any other non-digit is the thousands separator.
      const hasDecimals = /[^\d]89$/.test(digits);
      const decimalSep = hasDecimals ? digits.slice(-3, -2) : '';
      const thousandsSep = seps.split('').find(c => c !== decimalSep) || '';
      const n = Math.round(Number(cents) || 0);
      const sign = n < 0 ? '-' : '';
      const abs = Math.abs(n);
      const whole = hasDecimals ? Math.floor(abs / 100) : Math.round(abs / 100);
      const frac = hasDecimals ? String(abs % 100).padStart(2, '0') : '';
      const wholeStr = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return sign + prefix + wholeStr + (hasDecimals ? decimalSep + frac : '') + suffix;
    },

    // Format in the PRESENTMENT currency. The file's own formatMoney is a
    // hardcoded '$', which is wrong on every non-AUD market (a third of
    // orders); bundle-discounts.js documents the same trap for the drawer.
    // 'en' locale + narrowSymbol is what matches the server-rendered `money`
    // filter beside it ("$65.00", not "A$65.00"). Falls back to `fallback`
    // when the currency is unknown or the engine lacks narrowSymbol.
    money(cents, currency, fallback) {
      if (currency) {
        try {
          return new Intl.NumberFormat('en', {
            style: 'currency', currency, currencyDisplay: 'narrowSymbol',
          }).format(cents / 100);
        } catch (e) { /* fall through */ }
      }
      return fallback(cents);
    },

    // Variant name for the sticky bar. Shopify's single-variant placeholder
    // is the literal "Default Title" and must never be shown.
    variantLabel(variant) {
      const t = variant && (variant.title || variant.option1) || '';
      return t === 'Default Title' ? '' : t;
    },

    // Sticky bar visibility. Shown only once the main form has scrolled
    // ABOVE the viewport (formBottom < 0): not while it is on screen, not
    // while the shopper is still up in the gallery, not while the drawer is
    // open. Scroll direction is deliberately not an input.
    stickyBarVisible({ formBottom, drawerOpen }) {
      if (drawerOpen) return false;
      return typeof formBottom === 'number' && formBottom < 0;
    },

    // Sticky CTA label: mirror the main button, except that an idle main
    // button with the add-on ticked reads "Add both" so a tap from the bar
    // never silently adds a second line.
    stickyCtaLabel({ mainText, mainAvailableText, addonTicked, addBothText }) {
      const idle = (mainText || '').trim() === (mainAvailableText || '').trim();
      if (idle && addonTicked && addBothText) return addBothText;
      return mainText;
    },

    // Fold the main form and the add-on into the JSON shape /cart/add.js
    // accepts. The main line keeps everything the form would have posted —
    // properties[...] and selling_plan included — so ticking the box never
    // strips a line-item property or a subscription. Add-on is always
    // quantity 1: the shopper is wrapping ONE highchair, however many
    // footrests they buy. Returns null when either id is unusable, and the
    // caller then falls back to the plain form post.
    buildItemsPayload(formData, addonVariantId) {
      const id = Number(formData.get('id'));
      const addonId = Number(addonVariantId);
      if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(addonId) || addonId <= 0) return null;
      const main = { id, quantity: Math.max(1, parseInt(formData.get('quantity'), 10) || 1) };
      for (const [key, value] of formData.entries()) {
        const prop = /^properties\[(.+)\]$/.exec(key);
        if (prop) {
          (main.properties = main.properties || {})[prop[1]] = value;
        } else if (key === 'selling_plan' && value) {
          main.selling_plan = value;
        }
      }
      return { items: [main, { id: addonId, quantity: 1 }] };
    },
  };
  window.PDPV2Helpers = PDPV2Helpers;

  class PDPManager {
    constructor(productJSON) {
      this.product = productJSON;
      this.currentVariant = null;
      this.selectedOptions = {};
      
      // Build variant map for O(1) lookups
      this.variantMap = new Map(
        this.product.variants.map(v => [v.id, v])
      );
      
      // Cache DOM queries
      this.cachedElements = {};
      
      // Debounce timer
      this.variantUpdateTimeout = null;
      
      // Initialize
      this.init();
    }

    init() {
      // Set initial variant
      // `product | json` carries no selected_or_first_available_variant, so
      // that key is always undefined here. Resolve the initial variant from
      // the form's hidden id (which Liquid set from the real selection —
      // ?variant= included), then fall back to the first available variant.
      // The hidden id wins: it is the server's actual selection.
      const idInput = document.querySelector('[data-pdp-v2-form] [data-variant-id]');
      this.currentVariant = this.variantMap.get(Number(idInput && idInput.value))
        || this.product.selected_or_first_available_variant
        || this.product.variants.find(v => v.available)
        || this.product.variants[0]
        || null;

      // Cache DOM elements
      this.cacheElements();

      // Initialize components
      this.initGallery();
      this.initQuantitySelector();
      this.initVariantSelection();
      this.initFormSubmission();
      this.initStickyBar();
      this.initAddonRow();
    }

    protectSwatches() {
      // Prevent theme JS from removing or hiding PDP v2 swatches
      const container = document.querySelector('[data-pdp-v2-protected="true"]');
      if (!container) {
        console.warn('[PDP v2] No protected container found!');
        return;
      }

      // Watch for mutations on the container
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            const target = mutation.target;
            const attrName = mutation.attributeName;
            const newValue = target.getAttribute(attrName);
            console.warn('[PDP v2] Attribute changed:', {
              element: target.tagName + '.' + target.className,
              attribute: attrName,
              newValue: newValue,
              oldValue: mutation.oldValue
            });

            // If style attribute is being set to hide element, prevent it
            if (attrName === 'style' && newValue && (newValue.includes('display: none') || newValue.includes('display:none'))) {
              console.error('[PDP v2] BLOCKING display:none on swatch!');
              target.style.display = 'block';
            }
          }

          if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
            console.error('[PDP v2] Children removed from protected container!', mutation.removedNodes);
          }
        });
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
        attributeOldValue: true
      });

      // Override remove() method on protected swatches
      const swatches = container.querySelectorAll('.c-swatches, .swatch');
      swatches.forEach(swatch => {
        const originalRemove = swatch.remove.bind(swatch);
        swatch.remove = function() {
          console.error('[PDP v2] Prevented swatch.remove() call!',  new Error().stack);
          return false;
        };

        // Prevent parent removal
        if (swatch.parentElement) {
          const originalParentRemove = swatch.parentElement.remove.bind(swatch.parentElement);
          swatch.parentElement.remove = function() {
            console.error('[PDP v2] Prevented parent.remove() call!', new Error().stack);
            return false;
          };
        }
      });

      console.log('[PDP v2] Protected', swatches.length, 'swatch containers with MutationObserver');
    }

    cacheElements() {
      this.cachedElements = {
        saleBadges: document.querySelectorAll('[data-sale-badge]'),
        savePills: document.querySelectorAll('[data-save-pill]'),
        savingsPercent: document.querySelectorAll('[data-savings-percent]'),
        savingsAmount: document.querySelectorAll('[data-savings-amount]'),
        priceElements: document.querySelectorAll('[data-price]'),
        comparePriceElements: document.querySelectorAll('[data-compare-price]'),
        priceSavings: document.querySelectorAll('[data-price-savings]'),
        variantIdInputs: document.querySelectorAll('[data-variant-id]'),
        addToCartButtons: document.querySelectorAll('[data-add-to-cart], [data-sticky-cta]')
      };
    }

    /**
     * Gallery initialization
     * Handles thumbnail clicks and hero image switching
     */
    initGallery() {
      // Mobile gallery
      const mobileHero = document.querySelector('[data-pdp-v2-hero]');
      const mobileThumbnails = document.querySelectorAll('[data-pdp-v2-thumbnail]');
      
      if (mobileHero && mobileThumbnails.length > 0) {
        console.log('[PDP v2] Mobile gallery initialized');
        this.setupThumbnailSwitching(mobileHero, mobileThumbnails, 'mobile');
      }

      // Desktop gallery
      const desktopHero = document.querySelector('[data-pdp-v2-desktop-hero]');
      const desktopThumbnails = document.querySelectorAll('[data-pdp-v2-desktop-thumbnail]');
      
      if (desktopHero && desktopThumbnails.length > 0) {
        console.log('[PDP v2] Desktop gallery initialized');
        this.setupThumbnailSwitching(desktopHero, desktopThumbnails, 'desktop');
      }
    }

    /**
     * Setup thumbnail click handlers
     */
    setupThumbnailSwitching(hero, thumbnails, context) {
      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', (e) => {
          const button = e.currentTarget;
          const mediaType = button.dataset.mediaType;
          const mediaUrl = button.dataset.mediaUrl;
          const mediaSrcset = button.dataset.mediaSrcset;

          // Update hero image (no active state visual per POC)
          if (mediaType === 'image' && mediaUrl) {
            this.updateHeroImage(hero, mediaUrl, mediaSrcset, context);
          }

          // Announce change for screen readers
          this.announceToScreenReader('Image changed');
        });
      });
    }

    /**
     * Update hero image
     * Handles both src and srcset to ensure responsive images work correctly
     */
    updateHeroImage(hero, url, srcset, context) {
      // Updated to use new BEM class names from architecture refactor
      const imageClass = '.c-gallery__hero-image';

      let heroImage = hero.querySelector(imageClass);

      // Fallback: try generic img selector if specific class not found
      if (!heroImage) {
        heroImage = hero.querySelector('img');
      }

      if (heroImage) {
        heroImage.src = url;
        if (srcset) {
          heroImage.srcset = srcset;
        } else if (url && url.includes('cdn.shopify.com')) {
          // Generate srcset for Shopify CDN URLs to maintain responsive images
          // Remove any existing width parameter from URL
          const baseUrl = url.replace(/[?&]width=\d+/g, '').replace(/\?$/, '');
          const separator = baseUrl.includes('?') ? '&' : '?';
          heroImage.srcset = `${baseUrl}${separator}width=800 800w,
                              ${baseUrl}${separator}width=1200 1200w,
                              ${baseUrl}${separator}width=1600 1600w`;
        } else {
          // Non-Shopify URLs: remove stale srcset to avoid showing wrong image
          heroImage.removeAttribute('srcset');
        }
      } else {
        console.error('[PDP v2] Could not find hero image element', { context, hero, imageClass });
      }
    }

    /**
     * Initialize quantity selector
     */
    initQuantitySelector() {
      const quantityInputs = document.querySelectorAll('.c-quantity__input');
      const decrementButtons = document.querySelectorAll('.c-quantity__button[data-action="decrement"]');
      const incrementButtons = document.querySelectorAll('.c-quantity__button[data-action="increment"]');

      quantityInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.validateQuantity(input);
        });
      });

      decrementButtons.forEach(button => {
        button.addEventListener('click', () => {
          const input = button.parentElement.querySelector('.c-quantity__input');
          this.changeQuantity(input, -1);
        });
      });

      incrementButtons.forEach(button => {
        button.addEventListener('click', () => {
          const input = button.parentElement.querySelector('.c-quantity__input');
          this.changeQuantity(input, 1);
        });
      });
    }

    /**
     * Change quantity
     */
    changeQuantity(input, delta) {
      const currentValue = parseInt(input.value) || 1;
      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 999;
      const newValue = Math.max(min, Math.min(max, currentValue + delta));
      
      input.value = newValue;
      this.validateQuantity(input);
    }

    /**
     * Validate quantity input
     */
    validateQuantity(input) {
      const value = parseInt(input.value);
      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 999;

      if (isNaN(value) || value < min) {
        input.value = min;
      } else if (value > max) {
        input.value = max;
      }
    }

    /**
     * Update product images based on variant selection
     * Works for both regular products and bundles
     *
     * Priority order for BUNDLES (when swatch is clicked):
     *   1. Clicked swatch's data-variant-image (shows specific component user clicked)
     *   2. variant.featured_media (bundle's main product photo)
     *   3. Fallback to other swatch groups or featured_image
     *
     * Priority order for REGULAR products:
     *   1. variant.featured_media (the variant's assigned photo)
     */
    updateProductImages(variant) {
      // Find hero images for both mobile and desktop
      const mobileHero = document.querySelector('[data-pdp-v2-hero]');
      const desktopHero = document.querySelector('[data-pdp-v2-desktop-hero]');

      // BUNDLES: Check clicked swatch's image FIRST
      // WHY: Bundle variants share featured_media by cushion pattern. When user clicks
      // a leg wrap swatch, we want to show the leg wrap image, not just the bundle photo.
      // Without this, clicking leg wrap swatches appears to do nothing.
      if (this.product.handle.includes('bundle') && this.lastClickedSwatch) {
        const clickedImageUrl = this.lastClickedSwatch.dataset?.variantImage ||
                                this.lastClickedSwatch.querySelector('.wetheme-custom-radio')?.dataset?.variantImage;

        if (clickedImageUrl) {
          const imageUrl = clickedImageUrl.startsWith('//') ? 'https:' + clickedImageUrl : clickedImageUrl;

          if (mobileHero) {
            this.updateHeroImage(mobileHero, imageUrl, null, 'mobile');
          }
          if (desktopHero) {
            this.updateHeroImage(desktopHero, imageUrl, null, 'desktop');
          }
          return;
        }
      }

      // Priority 1: Use variant's featured_media (works for both regular products and bundles)
      if (variant && variant.featured_media && variant.featured_media.preview_image) {
        const src = variant.featured_media.preview_image.src;
        // Handle both {width} placeholder format and direct URLs
        const imageUrl = src.includes('{width}')
          ? src.replace('{width}', '1200')
          : src;
        const srcset = src.includes('{width}')
          ? `${src.replace('{width}', '800')} 800w,
             ${src.replace('{width}', '1200')} 1200w,
             ${src.replace('{width}', '1600')} 1600w`
          : null;

        // Update mobile gallery
        if (mobileHero) {
          this.updateHeroImage(mobileHero, imageUrl, srcset, 'mobile');
        }
        // Update desktop gallery
        if (desktopHero) {
          this.updateHeroImage(desktopHero, imageUrl, srcset, 'desktop');
        }
        return;
      }

      // Priority 2: For bundles without clicked swatch, try other swatch groups
      if (this.product.handle.includes('bundle')) {
        const swatchGroups = document.querySelectorAll('.swatch[data-option-index]');

        // Check all option groups for variant images
        for (const group of swatchGroups) {
          const checkedRadio = group.querySelector('.wetheme-custom-radio:checked');
          const swatch = checkedRadio?.closest('.swatch-element');
          const variantImageUrl = swatch?.dataset?.variantImage || checkedRadio?.dataset?.variantImage;

          if (variantImageUrl) {
            const imageUrl = variantImageUrl.startsWith('//') ? 'https:' + variantImageUrl : variantImageUrl;

            if (mobileHero) {
              this.updateHeroImage(mobileHero, imageUrl, null, 'mobile');
            }
            if (desktopHero) {
              this.updateHeroImage(desktopHero, imageUrl, null, 'desktop');
            }
            return;
          }
        }

        // Priority 3: Try variant's featured_image (legacy API structure)
        if (variant && variant.featured_image && variant.featured_image.src) {
          const variantImage = variant.featured_image.src.replace('//', 'https://');

          if (mobileHero) {
            this.updateHeroImage(mobileHero, variantImage, null, 'mobile');
          }
          if (desktopHero) {
            this.updateHeroImage(desktopHero, variantImage, null, 'desktop');
          }
          return;
        }
      }

      console.log('[PDP v2] No image update needed - no featured_media or variant image');
    }

    /**
     * Screen reader announcement utility
     */
    announceToScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      document.body.appendChild(announcement);

      setTimeout(() => {
        announcement.remove();
      }, 1000);
    }

    /**
     * Get product JSON
     * Utility for accessing product data
     */
    getProduct() {
      return this.product;
    }

    /**
     * Get current variant
     */
    getCurrentVariant() {
      return this.currentVariant;
    }

    initVariantSelection() {
      // Handle dropdown selectors (if present)
      const selectors = document.querySelectorAll('[data-option-select]');
      selectors.forEach(select => {
        select.addEventListener('change', () => {
          this.handleVariantChange();
        });
      });

      // Handle theme's swatch radio buttons
      const swatchRadios = document.querySelectorAll('.wetheme-custom-radio');
      if (swatchRadios.length > 0) {
        swatchRadios.forEach(radio => {
          radio.addEventListener('change', (e) => {
            // Store reference to clicked swatch element for bundle image updates
            const clickedSwatch = e.target.closest('.swatch-element');
            this.handleVariantChange(clickedSwatch);
          });
        });
      }
    }

    handleVariantChange(clickedSwatchElement = null) {
      let options = [];
      this.lastClickedSwatch = clickedSwatchElement; // Store for image update

      // Try dropdown selectors first
      const selectors = document.querySelectorAll('[data-option-select]');
      if (selectors.length > 0) {
        options = Array.from(selectors).map(select => select.value);
      } else {
        // Fall back to radio button swatches (theme system)
        // Group by option-index data attribute
        const swatchGroups = document.querySelectorAll('.swatch[data-option-index]');
        swatchGroups.forEach(group => {
          const optionIndex = parseInt(group.dataset.optionIndex);
          const checkedRadio = group.querySelector('.wetheme-custom-radio:checked');
          if (checkedRadio) {
            options[optionIndex] = checkedRadio.value;
          }
        });
      }

      const variant = this.product.variants.find(v => {
        return v.options.every((option, index) => option === options[index]);
      });

      if (variant) {
        clearTimeout(this.variantUpdateTimeout);

        this.variantUpdateTimeout = setTimeout(() => {
          this.currentVariant = variant;
          this.updateVariantUI(variant);
        }, 50);
      }
    }

    updateVariantUI(variant) {
      requestAnimationFrame(() => {
        const {
          saleBadges, savePills, savingsPercent, savingsAmount,
          priceElements, comparePriceElements, priceSavings,
          variantIdInputs, addToCartButtons
        } = this.cachedElements;

        // Update product images - CRITICAL for bundle products
        this.updateProductImages(variant);

        // Keep the one-tap add-on matched to the selected finish
        this.syncAddonRow(variant);
        // ...and the kit offer card pointed at the matching kit variant
        this.syncKitOffer(variant);
        // ...and the sticky bar's finish/price
        this.syncStickyBar(variant);

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        const savingsAmountValue = onSale ? variant.compare_at_price - variant.price : 0;
        const savingsPercentValue = onSale ? Math.round((savingsAmountValue / variant.compare_at_price) * 100) : 0;

        priceElements.forEach(el => {
          el.textContent = this.formatMoney(variant.price);
        });

        // The server hides these with an inline display:none when the first variant is not on
        // sale, so toggling the class alone never revealed them on a sale variant.
        comparePriceElements.forEach(el => {
          if (onSale) {
            el.textContent = this.formatMoney(variant.compare_at_price);
            el.classList.remove('hidden');
            el.style.display = '';
          } else {
            el.classList.add('hidden');
          }
        });
        
        priceSavings.forEach(el => {
          if (onSale) {
            el.classList.remove('hidden');
            el.style.display = '';
          } else {
            el.classList.add('hidden');
          }
        });
        
        savingsAmount.forEach(el => {
          el.textContent = this.formatMoney(savingsAmountValue);
        });

        this.syncSeparately(variant);
        
        saleBadges.forEach(badge => {
          if (onSale) {
            badge.classList.remove('hidden');
          } else {
            badge.classList.add('hidden');
          }
        });
        
        savePills.forEach(pill => {
          if (onSale) {
            pill.classList.remove('hidden');
          } else {
            pill.classList.add('hidden');
          }
        });
        
        savingsPercent.forEach(el => {
          el.textContent = savingsPercentValue;
        });

        variantIdInputs.forEach(input => {
          input.value = variant.id;
        });

        addToCartButtons.forEach(button => {
          if (variant.available) {
            button.disabled = false;
            button.textContent = button.getAttribute('data-available-text') || 'Add to cart';
          } else {
            button.disabled = true;
            button.textContent = button.getAttribute('data-unavailable-text') || 'Sold out';
          }
        });
      });
    }

    // Presentment-currency formatter for every price this script rewrites.
    // Was a hardcoded '$': on any non-AUD market a finish change replaced
    // "€11.95" with "$11.95" — the same defect S46/S57 removed from the cart
    // drawer. Liquid product JSON prices are presentment-converted, so the
    // active currency is the right one to format them in.
    formatMoney(cents) {
      // Prefer the server's own format (per-market separators); Intl on the
      // active currency is the fallback, '$' the fallback of last resort.
      const form = document.querySelector('[data-pdp-v2-form]');
      const fromSample = PDPV2Helpers.moneyFromSample(cents, form && form.getAttribute('data-money-sample'));
      if (fromSample) return fromSample;
      const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return PDPV2Helpers.money(cents, currency, (c) => '$' + (c / 100).toFixed(2));
    }

    // BUG-FIX: Add retry logic for transient network failures
    // `payload` is either the form's FormData (single line) or a plain
    // { items: [...] } object (main product + add-on), which /cart/add.js
    // accepts as JSON.
    async addToCartWithRetry(payload, retryCount = 0) {
      const formData = payload;
      const isJson = !(payload instanceof FormData);
      const MAX_RETRIES = 2;
      const RETRY_DELAYS = [1000, 2000]; // Exponential backoff

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: isJson ? { 'Content-Type': 'application/json', 'Accept': 'application/json' } : undefined,
          body: isJson ? JSON.stringify(payload) : formData,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          return await response.json();
        } else if (response.status >= 500 && retryCount < MAX_RETRIES) {
          // Server error - retry
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[retryCount]));
          return this.addToCartWithRetry(payload, retryCount + 1);
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || 'Failed to add to cart');
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError' && retryCount < MAX_RETRIES) {
          // Timeout - retry
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[retryCount]));
          return this.addToCartWithRetry(payload, retryCount + 1);
        }
        throw error;
      }
    }

    initFormSubmission() {
      const form = document.querySelector('[data-pdp-v2-form]');
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const formData = new FormData(form);
        const addToCartButton = form.querySelector('[data-add-to-cart]');

        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.textContent = addToCartButton.getAttribute('data-adding-text') || 'Adding…';
        }

        try {
          // BUG-FIX: Use retry logic for transient failures
          const data = await this.addToCartWithRetry(this.buildCartPayload(formData));

          // Fetch complete cart state from /cart.js (not just the item added)
          const controller = new AbortController();
          const cartTimeoutId = setTimeout(() => controller.abort(), 10000);
          const cartResponse = await fetch('/cart.js', { signal: controller.signal });
          clearTimeout(cartTimeoutId);
          const freshCart = await cartResponse.json();

          // BUG-FIX: Use consistent payload format { detail: cart } not { detail: { cart } }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: freshCart }));

          // BUG-FIX: Emit cart:added event for gift card forms and analytics
          document.dispatchEvent(new CustomEvent('cart:added'));

          // Announce success to screen readers (WCAG 2.1 AA - 4.1.3 Status Messages)
          this.announceToScreenReader((addToCartButton && addToCartButton.getAttribute('data-sr-added')) || 'Item added to cart');

          // Open cart drawer after successful add to cart
          // CRITICAL: Pass freshCart (complete cart) not data (single item)
          if (window.wetheme && window.wetheme.updateCartDrawer) {
            window.wetheme.updateCartDrawer(freshCart);

            // Trigger cart drawer open event
            // TIMING: Standard cart animation delay (300ms per cart-system audit)
            const cartDrawer = document.querySelector('#cartSlideoutWrapper');
            if (cartDrawer) {
              setTimeout(() => {
                cartDrawer.dispatchEvent(new CustomEvent('cart:open'));
              }, 300);
            }
          }

          if (addToCartButton) {
            addToCartButton.textContent = '✓ ' + (addToCartButton.getAttribute('data-added-text') || 'Added');
            setTimeout(() => {
              addToCartButton.textContent = addToCartButton.getAttribute('data-available-text') || 'Add to cart';
              addToCartButton.disabled = false;
            }, 2000);
          }

          // window.wetheme: this theme never defines window.theme, so the cart
          // refresh below silently never ran.
          if (window.wetheme && window.wetheme.cart) {
            window.wetheme.cart.getCart();
          }
        } catch (error) {
          console.error('[PDP v2] Add to cart error:', error);
          // Announce error to screen readers
          this.announceToScreenReader(((addToCartButton && addToCartButton.getAttribute('data-sr-error')) || 'Error adding to cart:') + ' ' + error.message);

          if (addToCartButton) {
            if (error.name === 'AbortError') {
              addToCartButton.textContent = addToCartButton.getAttribute('data-timeout-text') || 'Timeout — try again';
            } else {
              addToCartButton.textContent = addToCartButton.getAttribute('data-error-text') || 'Error — try again';
            }
            addToCartButton.disabled = false;
          }
        }
      });
    }


    /* ------------------------------------------------------------------
       One-tap add-on row (snippets/pdp-v2-addon-row.liquid)
       ------------------------------------------------------------------ */

    initAddonRow() {
      const row = document.querySelector('[data-pdp-addon]');
      const json = document.querySelector('[data-pdp-addon-json]');
      if (!row || !json) return;

      try {
        this.addon = JSON.parse(json.textContent);
      } catch (error) {
        console.error('[PDP v2] Add-on JSON invalid, row disabled:', error);
        row.hidden = true;
        return;
      }

      this.addonElements = {
        row,
        toggle: row.querySelector('[data-addon-toggle]'),
        label: row.querySelector('[data-addon-label]'),
        price: row.querySelector('[data-addon-price]'),
        compare: row.querySelector('[data-addon-compare]'),
        discount: row.querySelector('[data-addon-discount]'),
        thumb: row.querySelector('[data-addon-thumb]'),
      };

      // Cart-level ladder: read, never restate. Hidden when the config is
      // not on the page rather than guessing a number. bundle-discounts.js is
      // a separate deferred script that usually lands AFTER this one, so the
      // first attempt sees no BundleConfig — retry once everything has loaded.
      this.fillAddonDiscount();
      window.addEventListener('load', () => this.fillAddonDiscount(), { once: true });

      // Eligible units already in the cart raise the tier this add will land
      // on. Seed from /cart.js once, then follow the same `cart:updated`
      // event the drawer uses (its detail is the fresh cart).
      // The cart is cached, not just counted: /cart.js can answer before
      // BundleConfig exists, and fillAddonDiscount recounts from the cache
      // once it does.
      this.addonCart = null;
      // Cart gate: the row hides while the cart already holds a product type
      // on the hide list (a cushion PDP once a cushion or insert is in the
      // cart, bundles included). Server-rendered for first paint; refreshed
      // from the same section render on every cart change, because /cart.js
      // cannot see inside a bundle line.
      this.addonCartBlocked = row.getAttribute('data-addon-cart-blocked') === 'true';
      const applyCart = (cart) => {
        this.addonCart = cart;
        this.fillAddonDiscount();
        // Optimistic: a payload can only add hide reasons, never lift one.
        if (PDPV2Helpers.cartBlocksAddon(PDPV2Helpers.cartPayloadTypes(cart), this.addon.hide_types)) {
          this.setAddonCartBlocked(true);
        }
        this.refreshAddonCartGate();
      };
      document.addEventListener('cart:updated', (e) => applyCart(e.detail && e.detail.cart ? e.detail.cart : e.detail));
      // The seed is only a seed: if a cart:updated has already landed by the
      // time this response arrives, the response is older than what we hold.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => (r.ok ? r.json() : null))
        .then(cart => { if (cart && this.addonCart === null) applyCart(cart); })
        .catch(() => { /* keep the conservative count */ });

      // The tier depends on how many units will be submitted, so the copy
      // follows the quantity stepper. The +/- buttons write the input value
      // directly without dispatching change, hence the click listeners; the
      // read is deferred a tick so it sees the value AFTER their handler ran.
      const form = document.querySelector('[data-pdp-v2-form]');
      const refresh = () => setTimeout(() => this.fillAddonDiscount(), 0);
      if (form) {
        form.querySelectorAll('.c-quantity__input').forEach(el => {
          el.addEventListener('change', refresh);
          el.addEventListener('input', refresh);
        });
        form.querySelectorAll('.c-quantity__button').forEach(el => el.addEventListener('click', refresh));
      }

      // `product | json` carries no selected_or_first_available_variant, so
      // this.currentVariant is undefined until the first swatch change. Resolve
      // the initial variant from the form so a box ticked on a fresh load adds
      // the add-on too.
      const idInput = document.querySelector('[data-pdp-v2-form] [data-variant-id]');
      const initial = this.currentVariant || this.variantMap.get(Number(idInput && idInput.value));
      this.syncAddonRow(initial);

      // The sticky bar's label depends on the box — including one the browser
      // restored as ticked (bfcache / form restore) before this ran.
      if (this.addonElements.toggle) {
        this.addonElements.toggle.addEventListener('change', () => this.syncStickyCta());
      }
      this.syncStickyCta();
    }

    setAddonCartBlocked(blocked) {
      if (this.addonCartBlocked === !!blocked) return;
      this.addonCartBlocked = !!blocked;
      this.syncAddonRow(this.currentVariant || this.addonLastVariant);
    }

    // Authoritative gate read: sections/pdp-addon-cart-types.liquid renders
    // every cart line's product type plus each bundle line's item_components.
    // Only fetched when the product has a hide list, so the footrest row (no
    // list) makes no extra request. Failures keep the current state.
    refreshAddonCartGate() {
      const hide = this.addon && this.addon.hide_types;
      if (!Array.isArray(hide) || !hide.length) return;
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      const seq = (this.addonGateSeq = (this.addonGateSeq || 0) + 1);
      fetch(PDPV2Helpers.cartTypesSectionUrl(root), { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
        .then(r => (r.ok ? r.text() : null))
        .then(html => {
          if (seq !== this.addonGateSeq) return; // a newer read is in flight
          const types = PDPV2Helpers.parseCartTypesSection(html);
          if (types) this.setAddonCartBlocked(PDPV2Helpers.cartBlocksAddon(types, hide));
        })
        .catch(() => { /* keep the server-rendered / last known state */ });
    }

    fillAddonDiscount() {
      const discount = this.addonElements && this.addonElements.discount;
      if (!discount) return;
      const config = window.BundleConfig;
      const qty = document.querySelector('[data-pdp-v2-form] [data-quantity-input]');
      this.addonCartUnits = PDPV2Helpers.cartEligibleUnits(this.addonCart, config);
      const mainEligible = PDPV2Helpers.typeEligible(this.addon.main_type, config);
      const addonEligible = PDPV2Helpers.typeEligible(this.addon.type, config);
      const count = PDPV2Helpers.unitCount(qty && qty.value, this.addonCartUnits, mainEligible, addonEligible);
      const percent = PDPV2Helpers.addonDiscountPercent({
        tiers: PDPV2Helpers.ladderTiers(config),
        quantityValue: qty && qty.value,
        cartUnits: this.addonCartUnits,
        mainEligible,
        addonEligible,
      });
      if (!(percent > 0)) { discount.hidden = true; return; }
      // "off both" reads wrong once more than two units are going in. The
      // wider template says "eligible items", not "everything": the ladder is
      // scoped to promo-eligible products, and the cart may hold excluded ones.
      const tpl = (count > 2 && discount.getAttribute('data-template-all'))
        || discount.getAttribute('data-template') || '%percent%% off both at cart';
      discount.textContent = tpl.replace('%percent%', String(percent));
      discount.hidden = false;
    }

    addonMoney(cents) {
      return this.formatMoney(cents);
    }

    // Kit offer card (snippets/pdp-v2-kit-offer.liquid). Liquid pre-renders, per
    // product variant, the matching kit variant's URL and market-formatted
    // prices; this only swaps them in, so no currency is formatted here.
    syncKitOffer(variant) {
      if (!variant) return;
      const root = document.querySelector('[data-kit-offer]');
      if (!root) return;
      if (!this.kitOfferMap) {
        try {
          this.kitOfferMap = JSON.parse(root.querySelector('[data-kit-offer-json]').textContent);
        } catch (e) {
          this.kitOfferMap = {};
        }
      }
      const entry = this.kitOfferMap[String(variant.id)];
      if (!entry) return;
      const link = root.querySelector('[data-kit-offer-link]');
      const price = root.querySelector('[data-kit-offer-price]');
      const compare = root.querySelector('[data-kit-offer-compare]');
      if (link) link.href = entry.url;
      const eyebrow = root.querySelector('[data-kit-offer-eyebrow]');
      if (eyebrow) {
        eyebrow.textContent = entry.contains ? eyebrow.dataset.upgrade : eyebrow.dataset.pair;
      }
      if (price) price.textContent = entry.price;
      if (compare) {
        compare.textContent = entry.compare;
        compare.hidden = !entry.compare;
      }
    }

    syncAddonRow(variant) {
      if (!this.addon || !this.addonElements || !variant) return;
      const { row, toggle, label, price, compare, thumb } = this.addonElements;

      this.addonLastVariant = variant;
      const hit = PDPV2Helpers.resolveAddonVariant(this.addon.variants, variant.option1);
      const match = hit && hit.variant;
      this.addonVariant = match;

      // No usable add-on variant (no finish match and no generic, or sold
      // out), or the cart already holds a hide-listed product: hide, never
      // substitute.
      if (!match || this.addonCartBlocked) {
        row.hidden = true;
        if (toggle) toggle.checked = false;
        this.syncStickyCta();
        return;
      }

      if (label) {
        // The matcher outcome picks the copy: a finish match says
        // "Add matching X in Bamboo"; the generic fallback just "Add X".
        const tpl = hit.byFinish
          ? (row.getAttribute('data-label-template') || '')
          : (row.getAttribute('data-label-plain') || row.getAttribute('data-label-template') || '');
        label.textContent = tpl.replace('%finish%', match.option1);
      }
      if (price) price.textContent = '+' + this.addonMoney(match.price);
      if (compare) {
        const onSale = match.compare_at_price && match.compare_at_price > match.price;
        compare.textContent = onSale ? this.addonMoney(match.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      if (thumb) {
        // Never leave the previous finish's picture beside a new finish.
        if (match.image) thumb.src = match.image; else thumb.removeAttribute('src');
        thumb.hidden = !match.image;
      }
      row.hidden = false;
    }

    // FormData for a plain add; an `items` array when the add-on is ticked.
    buildCartPayload(formData) {
      const toggle = this.addonElements && this.addonElements.toggle;
      const addon = this.addonVariant;
      const wantAddon = !!(toggle && toggle.checked && addon && addon.available
        && this.addonElements.row && !this.addonElements.row.hidden);
      if (!wantAddon) return formData;
      return PDPV2Helpers.buildItemsPayload(formData, addon.id) || formData;
    }

    initStickyBar() {
      const stickyBar = document.querySelector('[data-pdp-sticky-bar]');
      const stickyCta = document.querySelector('[data-sticky-cta]');
      const mainForm = document.querySelector('[data-pdp-v2-form]');

      if (!stickyBar || !mainForm) return;

      this.stickyElements = {
        bar: stickyBar,
        cta: stickyCta,
        variant: stickyBar.querySelector('[data-sticky-variant]'),
        price: stickyBar.querySelector('[data-sticky-price]'),
        compare: stickyBar.querySelector('[data-sticky-compare]'),
      };

      // Visibility: driven by where the main CTA BUTTON is, not by scroll
      // direction. The button, not the form: a form taller than the viewport
      // could keep its bottom on screen after the button had gone.
      // IntersectionObserver fires on every visibility edge; the rect tells us
      // which side of the viewport it left by. Scroll/resize/visualViewport
      // listeners are belt-and-braces for coalesced IO callbacks (fast flings)
      // and for viewport-height changes that do not scroll (iOS URL bar).
      const anchor = mainForm.querySelector('[data-add-to-cart]') || mainForm;
      const update = () => {
        const drawerOpen = document.documentElement.classList.contains('slideout-open');
        const formBottom = anchor.getBoundingClientRect().bottom;
        stickyBar.style.display = PDPV2Helpers.stickyBarVisible({ formBottom, drawerOpen }) ? 'block' : 'none';
      };
      let ticking = false;
      const scheduled = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => { update(); ticking = false; });
      };
      const disposers = [];
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(update, { threshold: [0, 1] });
        io.observe(anchor);
        disposers.push(() => io.disconnect());
      }
      const on = (target, type) => {
        target.addEventListener(type, scheduled, { passive: true });
        disposers.push(() => target.removeEventListener(type, scheduled));
      };
      on(window, 'scroll');
      on(window, 'resize');
      if (window.visualViewport) {
        on(window.visualViewport, 'resize');
        on(window.visualViewport, 'scroll');
      }
      // Drawer open/close toggles html.slideout-open
      const mo = new MutationObserver(update);
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      disposers.push(() => mo.disconnect());
      // Design mode reloads the section and builds a new PDPManager; the old
      // one must let go of window/document (initPDPV2 calls destroy()).
      this._stickyDispose = () => disposers.splice(0).forEach(fn => fn());
      update();

      if (stickyCta) {
        stickyCta.addEventListener('click', () => {
          const mainButton = mainForm.querySelector('[data-add-to-cart]');
          if (mainButton && !mainButton.disabled) {
            // BUG-FIX: Sync sticky CTA loading state with main button
            stickyCta.disabled = true;
            stickyCta.textContent = mainButton.getAttribute('data-adding-text') || 'Adding…';
            mainButton.click();
          }
        });

        // BUG-FIX: Watch for main button state changes to sync with sticky CTA
        const mainButton = mainForm.querySelector('[data-add-to-cart]');
        if (mainButton) {
          const observer = new MutationObserver(() => this.syncStickyCta());
          observer.observe(mainButton, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true
          });
        }
        this.syncStickyCta();
      }

      this.syncStickyBar(this.currentVariant);
    }

    destroy() {
      if (this._stickyDispose) this._stickyDispose();
    }

    // Mirror the main button into the bar, with the add-on-aware label.
    syncStickyCta() {
      const cta = this.stickyElements && this.stickyElements.cta;
      const mainButton = document.querySelector('[data-pdp-v2-form] [data-add-to-cart]');
      if (!cta || !mainButton) return;
      const toggle = this.addonElements && this.addonElements.toggle;
      const addonTicked = !!(toggle && toggle.checked && this.addonElements.row && !this.addonElements.row.hidden);
      cta.disabled = mainButton.disabled;
      cta.textContent = PDPV2Helpers.stickyCtaLabel({
        mainText: mainButton.textContent,
        mainAvailableText: mainButton.getAttribute('data-available-text'),
        addonTicked,
        addBothText: cta.getAttribute('data-add-both-text'),
      });
    }

    // Bundles whose compare-at is hidden (EEA, #319) show "bought separately" from a
    // server-rendered map of variant id -> live component total in the shopper's currency
    // (snippets/pdp-v2-title-price.liquid). A variant with no entry hides the line.
    syncSeparately(variant) {
      const line = document.querySelector('[data-separately]');
      const mapEl = document.querySelector('[data-separately-map]');
      if (!line || !mapEl || !variant) return;
      if (!this.separatelyMap) {
        try { this.separatelyMap = JSON.parse(mapEl.textContent); } catch (e) { this.separatelyMap = {}; }
      }
      const total = this.separatelyMap[String(variant.id)];
      if (!total || total <= variant.price) {
        line.hidden = true;
        return;
      }
      line.textContent = (line.getAttribute('data-template') || '')
        .replace('[[T]]', this.formatMoney(total))
        .replace('[[S]]', this.formatMoney(total - variant.price));
      line.hidden = false;
    }

    // Finish + price in the bar follow the selected variant. The server
    // rendered them once; without this the bar showed the first variant's
    // price after any swatch change.
    syncStickyBar(variant) {
      const el = this.stickyElements;
      if (!el || !variant) return;
      if (el.variant) el.variant.textContent = PDPV2Helpers.variantLabel(variant);
      if (el.price) el.price.textContent = this.formatMoney(variant.price);
      if (el.compare) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.compare.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
        el.compare.hidden = !onSale;
      }
    }
  }

  /**
   * Initialize PDP Manager when product JSON is available
   */
  function initPDPV2() {
    const productJSON = document.getElementById('product-json');
    
    if (!productJSON) {
      console.warn('[PDP v2] Product JSON not found');
      return;
    }

    try {
      const product = JSON.parse(productJSON.textContent);
      if (window.PDPManagerV2 && typeof window.PDPManagerV2.destroy === 'function') {
        window.PDPManagerV2.destroy();
      }
      window.PDPManagerV2 = new PDPManager(product);
      
      // Dispatch event for other scripts
      document.dispatchEvent(new CustomEvent('pdp-v2:initialized', {
        detail: { manager: window.PDPManagerV2 }
      }));
    } catch (error) {
      console.error('[PDP v2] Failed to parse product JSON:', error);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPDPV2);
  } else {
    initPDPV2();
  }

  // Re-initialize on Shopify section re-render (theme editor)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initPDPV2);
  }

  /**
   * Initialize Selected Color Name Display
   * Updates the legend label to show selected swatch value
   */
  function initSelectedColorDisplay() {
    // Find all swatch inputs
    const swatchInputs = document.querySelectorAll('.swatch-element input[type="radio"]');

    swatchInputs.forEach(input => {
      input.addEventListener('change', function() {
        // Find the parent fieldset to locate the legend
        const fieldset = this.closest('fieldset');
        if (!fieldset) return;

        const selectedValue = this.value;

        // Find the legend's selected value span
        const legend = fieldset.querySelector('[data-swatch-legend] [data-selected-value]');

        if (legend) {
          // Update the legend to show selected value
          legend.textContent = selectedValue;
        }
      });
    });
  }

  // Initialize selected color display
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSelectedColorDisplay);
  } else {
    initSelectedColorDisplay();
  }

})(window, document);